/* ============================================================================
   Furniva cart drawer behaviour.

   Quantity changes and removals go through Shopify's own Cart AJAX endpoint
   (`/cart/change.js`) and the Section Rendering API refreshes the drawer from
   the server, so the cart totals, line prices and availability are always
   Shopify's own — this file never computes a total itself.

   Every mutation has a visible state: updating, removed, and a failure message
   that restores the previous quantity rather than leaving a number that was
   never accepted.
   ============================================================================ */
(function () {
  'use strict';

  var SECTION = 'cart-drawer-section';

  function $(s, sc) { return (sc || document).querySelector(s); }
  function $$(s, sc) { return Array.prototype.slice.call((sc || document).querySelectorAll(s)); }

  var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root
    ? window.Shopify.routes.root : '/';
  var refreshPending = null;

  function drawerEl() { return document.querySelector('theme-drawer#cart-drawer'); }

  /* ------------------------------------------------------------- mobile -- *

     There is no cart drawer on a phone.

     Owner decision, 2026-09-29. At this width the drawer covered the whole
     screen, so it was a full page pretending to be an overlay — with a
     narrower column, a scroll position of its own and a close button where the
     back gesture should be. The cart page is the same information with the
     browser's own navigation around it.

     Every control that opens it is already `<a href="{{ routes.cart_url }}">`,
     so refusing to intercept is all it takes: the link does what a link does.
     759.98px matches MOBILE_QUERY in furniva.js and the marquee rule in
     furniva-chrome.css, so one definition of "phone" holds across the chrome.
   * ----------------------------------------------------------------------- */
  var MOBILE = '(max-width: 759.98px)';
  function onMobile() {
    return typeof window.matchMedia === 'function' && window.matchMedia(MOBILE).matches;
  }

  /* ------------------------------------------------------------- refresh -- */
  // Replace the drawer's markup with the server's, so what the customer sees is
  // what Shopify actually holds.
  function refresh() {
    if (refreshPending) return refreshPending;
    refreshPending = fetch(root + '?sections=' + SECTION, { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        if (!data || !data[SECTION]) return;
        var parsed = new DOMParser().parseFromString(data[SECTION], 'text/html');
        var next = parsed.querySelector('[data-fv-cartdrawer]');
        var current = $('[data-fv-cartdrawer]');
        if (next && current) current.replaceWith(next);
        paintCount();
      })
      .finally(function () { refreshPending = null; });
    return refreshPending;
  }

  function paintCount() {
    fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        $$('[data-fv-cart-count]').forEach(function (n) { n.textContent = cart.item_count; });
      })
      .catch(function () { /* the header count simply stays as it was */ });
  }

  /* -------------------------------------------------------------- states -- */
  function setState(line, text, tone) {
    if (!line) return;
    var el = line.querySelector('[data-fv-line-state]');
    if (!el) return;
    el.textContent = text || '';
    el.className = 'fv-cartline__state' + (tone ? ' is-' + tone : '');
  }

  function busy(line, on) {
    if (!line) return;
    line.classList.toggle('is-busy', !!on);
    $$('button, input', line).forEach(function (c) { c.disabled = !!on; });
  }

  /* ------------------------------------------------------------- mutate --- */
  function change(key, quantity, line, previous) {
    busy(line, true);
    setState(line, quantity === 0 ? 'Removing…' : 'Updating…', 'busy');

    return fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error(e.description || e.message || 'Could not update'); });
        return r.json();
      })
      .then(function () { return refresh(); })
      .catch(function (error) {
        busy(line, false);
        // Put the number back: showing a quantity the cart never accepted would
        // misrepresent the order.
        var input = line && line.querySelector('[data-fv-qty-select]');
        if (input && previous != null) input.value = previous;
        setState(line, error.message || 'Could not update this line.', 'error');
      });
  }

  /* ------------------------------------------------------------ handlers -- */
  document.addEventListener('click', function (event) {
    var closer = event.target.closest('[data-fv-cartdrawer-close]');
    if (closer) {
      event.preventDefault();
      // Through closeDrawer so the exit animation runs before the panel hides.
      closeDrawer();
      return;
    }

    var remove = event.target.closest('[data-fv-remove]');
    if (remove) {
      event.preventDefault();
      var rLine = remove.closest('[data-fv-line]');
      change(remove.dataset.key, 0, rLine, null);
      return;
    }
  });

  // The quantity is a select, so a change is already a decision. The previous
  // value travels on the element, so a refused change can put it back.
  document.addEventListener('change', function (event) {
    var select = event.target.closest('[data-fv-qty-select]');
    if (!select || !select.dataset.key) return;
    var line = select.closest('[data-fv-line]');
    var next = parseInt(select.value, 10);
    if (isNaN(next) || next < 0) next = 0;
    var previous = parseInt(select.dataset.fvPrev, 10);
    change(select.dataset.key, next, line, isNaN(previous) ? null : previous);
  });


  /* Horizon owns the native dialog, focus, Escape and backdrop. Keep that
     machinery intact; styling an unopened <dialog> was the cart regression. */
  function openDrawer() {
    var drawer = drawerEl();
    if (!drawer || typeof drawer.open !== 'function') return;
    if (!drawer.isOpen) drawer.open();
    var panel = drawer.querySelector('dialog');
    if (panel && refreshPending) {
      panel.classList.add('is-updating');
      panel.setAttribute('aria-busy', 'true');
      refreshPending.finally(function () {
        panel.classList.remove('is-updating');
        panel.removeAttribute('aria-busy');
      }).catch(function () {});
    }
  }

  function closeDrawer() {
    var drawer = drawerEl();
    if (drawer && typeof drawer.close === 'function') drawer.close();
  }

  /* A press anywhere outside the panel closes the drawer.

     This drawer is a non-modal <dialog>: it is shown, not shown-as-modal, so
     there is no backdrop between it and the page and a press outside lands on
     whatever is there — the hero, the header, a product card. So "outside" is
     decided by asking whether the pressed node is inside the panel, not by
     looking for a backdrop that does not exist.

     The press has to both start and end outside. That matters for two things:
     selecting text in the drawer and releasing past its edge, and pressing a
     control inside the drawer that removes its own node from the document —
     by the time the click runs, that node is detached and would look external.
     Recording where the press began settles both. */
  var pressedOutside = false;

  function panelEl() {
    var drawer = drawerEl();
    return drawer ? drawer.querySelector('dialog') : null;
  }

  document.addEventListener('pointerdown', function (event) {
    var panel = panelEl();
    pressedOutside = !!(panel && panel.open && !panel.contains(event.target));
  }, true);

  document.addEventListener('click', function (event) {
    if (!pressedOutside) return;
    pressedOutside = false;

    var panel = panelEl();
    if (!panel || !panel.open) return;
    if (panel.contains(event.target)) return;
    // The bag is a way in, not a way out: pressing it while the drawer is
    // already open should leave it open rather than close and reopen.
    if (event.target.closest && event.target.closest('[data-fv-cart-open]')) return;

    closeDrawer();
  });

  // The header bag and anything else that opens the cart route through here.
  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-fv-cart-open]');
    if (!opener) return;
    if (onMobile()) return;                                   // the link goes to /cart
    var drawer = drawerEl();
    if (!drawer || typeof drawer.open !== 'function') return; // let /cart work before upgrade
    event.preventDefault();
    openDrawer(opener);
  }, true);

  // Adding from anywhere in the theme opens the drawer through the same path.
  // On a phone it does nothing rather than navigating: the only dispatcher is
  // an add-to-cart, and furniva-addfx.js deliberately does not interrupt
  // someone who is still shopping. Sending them to /cart would be exactly the
  // interruption it avoids.
  document.addEventListener('furniva:cart:open', function () {
    if (onMobile()) return;
    openDrawer(null);
  });

  /* Rotating a tablet, or dragging a desktop window narrow, while the drawer is
     open would strand it over a layout that no longer has one. */
  if (typeof window.matchMedia === 'function') {
    var mobileQuery = window.matchMedia(MOBILE);
    var onChange = function (event) { if (event.matches) closeDrawer(); };
    if (mobileQuery.addEventListener) mobileQuery.addEventListener('change', onChange);
    else if (mobileQuery.addListener) mobileQuery.addListener(onChange);
  }

  // Open immediately with a truthful loading state while server markup refreshes.
  window.furnivaCartDrawer = { open: openDrawer, close: closeDrawer, refresh: refresh };

  /* A drawer belongs to the page that opened it.

     Leaving one open and navigating meant arriving with it still up — and the
     back button is worse, because bfcache restores the whole document with the
     dialog exactly as it was. `pagehide` covers leaving under both a normal
     unload and a bfcache freeze; `pageshow` with `persisted` covers coming
     back to a frozen page. Neither touches how the drawer opens here and now. */
  window.addEventListener('pagehide', function () { closeDrawer(); });
  window.addEventListener('pageshow', function (event) {
    if (event && event.persisted) closeDrawer();
  });

  // Horizon still owns "add to cart"; when it fires, pull the drawer's markup
  // back from the server so the new line appears with real data.
  ['cart:update', 'cart:refresh', 'cart:add'].forEach(function (name) {
    document.addEventListener(name, function (event) {
      if (event && event.detail && event.detail.__furniva) return;
      refresh();
    });
  });
})();
